import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import { SITE_URL, buildBreadcrumbJsonLd } from '../utils/seo';
import { Spin, Tag } from 'antd';
import {
  EnvironmentOutlined,
  PhoneOutlined,
  ShopOutlined,
  CarOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import MainHeader from '../header/MainHeader';
import Footer from '../footer/Footer';
import { dealerService } from '../api/services/dealerService';

const ORANGE = '#fe5a1d';

type DealerProfile = Awaited<ReturnType<typeof dealerService.getDealerPublicProfile>>;

const DealerShop: React.FC = () => {
  const { dealerId } = useParams<{ dealerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [profile, setProfile] = useState<DealerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!dealerId) return;
    dealerService.getDealerPublicProfile(dealerId)
      .then(setProfile)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [dealerId]);

  if (loading) {
    return (
      <div className="home-page-light">
        <MainHeader />
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="home-page-light">
        <MainHeader />
        <div style={{ textAlign: 'center', padding: '120px 24px' }}>
          <ShopOutlined style={{ fontSize: 56, color: '#d9d9d9', display: 'block', marginBottom: 16 }} />
          <p style={{ fontSize: 18, fontWeight: 700, color: '#333' }}>{t('dealer_shop.not_found')}</p>
          <p style={{ color: '#888', marginBottom: 24 }}>{t('dealer_shop.not_found_desc')}</p>
          <button
            onClick={() => navigate(-1)}
            style={{ background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            {t('dealer_shop.go_back')}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const initial = profile.companyName.charAt(0).toUpperCase();
  const memberYear = new Date(profile.memberSince).getFullYear();

  const dealerBreadcrumb = buildBreadcrumbJsonLd([
    { name: 'AutoPlace', url: SITE_URL },
    { name: 'Dealers', url: `${SITE_URL}/mysearch?sellerType=dealer` },
    { name: profile.companyName, url: `${SITE_URL}/dealer/${dealerId}` },
  ]);

  return (
    <div className="home-page-light">
      <SEOHead
        title={`${profile.companyName} - Dealer i Verifikuar | AutoPlace`}
        description={`${profile.companyName} është dealer i verifikuar në AutoPlace${profile.city ? `, ${profile.city}` : ''}. ${profile.totalListings} makina aktive. Kontakto tani!`}
        canonical={`${SITE_URL}/dealer/${dealerId}`}
        jsonLd={dealerBreadcrumb}
      />
      <MainHeader />
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '100px 24px 60px' }}>

        {/* ── Hero banner ── */}
        <div style={{
          background: `linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)`,
          borderRadius: 20,
          padding: '40px 40px 32px',
          marginBottom: 32,
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Background decoration */}
          <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', background: `${ORANGE}18` }} />
          <div style={{ position: 'absolute', bottom: -30, right: 80, width: 120, height: 120, borderRadius: '50%', background: `${ORANGE}10` }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 28, position: 'relative', zIndex: 1, flexWrap: 'wrap' }}>
            {/* Avatar */}
            <div style={{
              width: 88, height: 88, borderRadius: 18,
              background: `linear-gradient(135deg, ${ORANGE}, #ff8a50)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 36, fontWeight: 800, color: '#fff',
              boxShadow: `0 8px 24px ${ORANGE}50`, flexShrink: 0,
            }}>
              {initial}
            </div>

            {/* Name + meta */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#fff' }}>{profile.companyName}</h1>
                <Tag style={{ background: `${ORANGE}22`, border: `1px solid ${ORANGE}`, color: ORANGE, borderRadius: 20, fontWeight: 700, fontSize: 11 }}>
                  {t('dealer_shop.verified_dealer')}
                </Tag>
              </div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {profile.city && (
                  <span style={{ color: '#aaa', fontSize: 14, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <EnvironmentOutlined /> {profile.city}, {profile.country}
                  </span>
                )}
                <span style={{ color: '#aaa', fontSize: 14, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <CalendarOutlined /> {t('dealer_shop.member_since', { year: memberYear })}
                </span>
                <span style={{ color: '#aaa', fontSize: 14, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <CarOutlined /> {t('dealer_shop.active_listings', { count: profile.totalListings })}
                </span>
              </div>
            </div>

            {/* Contact */}
            {profile.phone && (
              <a
                href={`tel:${profile.phone}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: ORANGE, color: '#fff', borderRadius: 12,
                  padding: '12px 20px', fontSize: 14, fontWeight: 700,
                  textDecoration: 'none', whiteSpace: 'nowrap',
                  boxShadow: `0 4px 16px ${ORANGE}50`,
                }}
              >
                <PhoneOutlined /> {profile.phone}
              </a>
            )}
          </div>
        </div>

        {/* ── Listings grid ── */}
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#222', marginBottom: 20 }}>
          {t('dealer_shop.all_listings')} <span style={{ fontSize: 14, fontWeight: 500, color: '#999' }}>({profile.totalListings})</span>
        </h2>

        {profile.listings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', background: '#fafafa', borderRadius: 16 }}>
            <CarOutlined style={{ fontSize: 48, color: '#d9d9d9', display: 'block', marginBottom: 12 }} />
            <p style={{ color: '#888', margin: 0 }}>{t('dealer_shop.no_active_listings')}</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 20,
          }}>
            {profile.listings.map(listing => (
              <div
                key={listing.id}
                onClick={() => navigate(`/listing/${listing.slug || listing.id}`)}
                style={{
                  background: '#fff',
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                  cursor: 'pointer',
                  border: '1px solid #f0f0f0',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)';
                }}
              >
                {/* Image */}
                <div style={{ height: 170, background: '#f5f5f5', overflow: 'hidden', position: 'relative' }}>
                  {listing.image ? (
                    <img
                      src={listing.image}
                      alt={listing.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CarOutlined style={{ fontSize: 40, color: '#ddd' }} />
                    </div>
                  )}
                  {listing.year && (
                    <span style={{
                      position: 'absolute', top: 10, left: 10,
                      background: 'rgba(0,0,0,0.6)', color: '#fff',
                      borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600,
                    }}>
                      {listing.year}
                    </span>
                  )}
                </div>

                {/* Info */}
                <div style={{ padding: '14px 16px' }}>
                  <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {listing.title}
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                    {listing.mileage != null && (
                      <span style={{ fontSize: 12, color: '#888' }}>{listing.mileage.toLocaleString()} km</span>
                    )}
                    {listing.fuelType && (
                      <span style={{ fontSize: 12, color: '#888' }}>{listing.fuelType}</span>
                    )}
                    {listing.transmission && (
                      <span style={{ fontSize: 12, color: '#888' }}>{listing.transmission}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: ORANGE }}>
                      {listing.price != null ? `€ ${listing.price.toLocaleString()}` : 'POA'}
                    </span>
                    {listing.city && (
                      <span style={{ fontSize: 11, color: '#aaa', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <EnvironmentOutlined style={{ fontSize: 10 }} /> {listing.city}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default DealerShop;
